<template>
  <transition name="lightbox">
    <div
      v-if="open"
      class="app-image-lightbox-overlay"
      :class="{ 'app-image-lightbox-overlay--interacting': isInteracting }"
      @click.self="onBackdropClick"
      @wheel.prevent="onWheel"
      @touchstart="onTouchStart"
      @touchmove.prevent="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <div class="app-image-lightbox-actions">
        <button type="button" class="app-image-lightbox-action" aria-label="Download" @click="onDownload">
          <app-icon icon="download" />
        </button>
        <button type="button" class="app-image-lightbox-action" aria-label="Close" @click="onClose">
          <app-icon icon="x" />
        </button>
      </div>
      <div
        class="app-image-lightbox-stage"
        :class="{ 'app-image-lightbox-stage--pannable': canPan }"
        :style="transformStyle"
      >
        <!-- The frame shrink-wraps the image, so a box positioned in percent
             lands on the same pixels it did on the thumbnail, at any zoom. -->
        <div class="app-image-lightbox-frame" :style="frameStyle">
          <img
            class="app-image-lightbox-image"
            :src="src"
            alt=""
            draggable="false"
            @dragstart.prevent
          />
          <div
            v-if="boxes.length > 0"
            class="app-image-lightbox-boxes"
            :style="boxesLayerStyle"
            aria-hidden="true"
          >
            <div
              v-for="(box, index) in boxes"
              :key="index"
              class="app-image-lightbox-box"
              :style="box.style"
              :title="box.label"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { Component } from "vue-property-decorator";
import { CatchError } from "@/app/shared/services/helper/catch-helper";
import { downloadImage } from "@/app/shared/services/helper/file-helper";
import { ImageLightboxTransformHelper } from "@/app/shared/services/helper/image-lightbox-transform-helper";
import { BaseComponent } from "@/app/shared/components/base-component/base-component";
import AppIcon from "@/app/shared/components/app-icon/app-icon.vue";
import type { ImageLightboxBox } from "./types";

@Component({
  name: "app-image-lightbox-overlay",
  components: {
    AppIcon,
  },
})
export default class AppImageLightboxOverlay extends BaseComponent {
  open = false;
  zoom = 1;
  panX = 0;
  panY = 0;
  src = "";
  downloadFilename = "";
  /** Set by the host component; empty for an image with nothing to mark. */
  boxes: ImageLightboxBox[] = [];

  isInteracting = false;
  isPanning = false;
  hasDragged = false;

  private pinchStartDistance = 0;
  private pinchStartZoom = 1;
  private panStartX = 0;
  private panStartY = 0;
  private panOriginX = 0;
  private panOriginY = 0;

  // Created in mounted() so `this` is bound to the real component instance
  // (class-field arrow functions are not, with this project's esnext target).
  private escapeKeyListener!: (event: KeyboardEvent) => void;

  get canPan(): boolean {
    return this.zoom > 1;
  }

  get transformStyle(): { transform: string } {
    return ImageLightboxTransformHelper.buildTransformStyle(this.zoom, this.panX, this.panY);
  }

  /** Shrink-wraps the image so percent boxes share its box. */
  get frameStyle(): Record<string, string> {
    return {
      position: "relative",
      display: "inline-block",
      lineHeight: "0",
    };
  }

  get boxesLayerStyle(): Record<string, string> {
    return {
      position: "absolute",
      inset: "0",
      zIndex: "1",
      pointerEvents: "none",
      overflow: "hidden",
    };
  }

  mounted() {
    this.escapeKeyListener = (event: KeyboardEvent) => this.onEscapeKey(event);
    // Capture phase so Esc closes the lightbox before parent modals handle it.
    window.addEventListener("keydown", this.escapeKeyListener, true);
  }

  beforeDestroy() {
    if (this.escapeKeyListener) {
      window.removeEventListener("keydown", this.escapeKeyListener, true);
    }
  }

  @CatchError()
  onClose() {
    this.open = false;
    this.resetTransform();
  }

  @CatchError()
  onEscapeKey(event: KeyboardEvent) {
    if (!this.open || event.key !== "Escape") {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.onClose();
  }

  onBackdropClick() {
    if (this.hasDragged) {
      this.hasDragged = false;
      return;
    }

    this.onClose();
  }

  onWheel(event: WheelEvent) {
    this.zoom = ImageLightboxTransformHelper.applyWheelZoom(this.zoom, event.deltaY);
    this.syncPanWithZoom();
  }

  onTouchStart(event: TouchEvent) {
    if (this.isActionTarget(event.target)) {
      return;
    }

    this.hasDragged = false;

    if (event.touches.length === 2) {
      this.isInteracting = true;
      this.isPanning = false;
      this.pinchStartDistance = ImageLightboxTransformHelper.getTouchDistance(event.touches[0], event.touches[1]);
      this.pinchStartZoom = this.zoom;
      return;
    }

    if (event.touches.length === 1) {
      this.isInteracting = true;
      this.startPan(event.touches[0].clientX, event.touches[0].clientY);
    }
  }

  onTouchMove(event: TouchEvent) {
    if (event.touches.length === 2 && this.pinchStartDistance > 0) {
      const currentDistance = ImageLightboxTransformHelper.getTouchDistance(event.touches[0], event.touches[1]);
      this.zoom = ImageLightboxTransformHelper.applyPinchZoom(
        this.pinchStartZoom,
        this.pinchStartDistance,
        currentDistance,
      );
      this.syncPanWithZoom();
      this.hasDragged = true;
      return;
    }

    if (event.touches.length === 1 && this.isPanning) {
      this.updatePan(event.touches[0].clientX, event.touches[0].clientY);
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (event.touches.length < 2) {
      this.pinchStartDistance = 0;
    }

    if (event.touches.length === 1) {
      this.isInteracting = true;
      this.startPan(event.touches[0].clientX, event.touches[0].clientY);
      return;
    }

    this.isPanning = false;

    if (event.touches.length === 0) {
      this.isInteracting = false;
    }
  }

  onPointerDown(event: PointerEvent) {
    if (event.pointerType === "touch" || event.button !== 0 || this.isActionTarget(event.target)) {
      return;
    }

    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.hasDragged = false;
    this.isInteracting = true;
    this.startPan(event.clientX, event.clientY);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.isPanning) {
      return;
    }

    const target = event.currentTarget as HTMLElement;
    if (!target.hasPointerCapture(event.pointerId)) {
      return;
    }

    event.preventDefault();
    this.updatePan(event.clientX, event.clientY);
  }

  onPointerUp(event: PointerEvent) {
    const target = event.currentTarget as HTMLElement;
    if (target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }

    this.isPanning = false;
    this.isInteracting = false;
  }

  @CatchError()
  async onDownload() {
    await downloadImage(this.src, this.downloadFilename || undefined);
  }

  resetTransform() {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.isInteracting = false;
    this.isPanning = false;
    this.hasDragged = false;
    this.pinchStartDistance = 0;
  }

  private startPan(clientX: number, clientY: number) {
    this.isPanning = true;
    this.panStartX = clientX;
    this.panStartY = clientY;
    this.panOriginX = this.panX;
    this.panOriginY = this.panY;
  }

  private updatePan(clientX: number, clientY: number) {
    if (
      ImageLightboxTransformHelper.hasExceededDragThreshold(this.panStartX, this.panStartY, clientX, clientY)
    ) {
      this.hasDragged = true;
    }

    this.panX = ImageLightboxTransformHelper.snapPanOffset(this.panOriginX + (clientX - this.panStartX));
    this.panY = ImageLightboxTransformHelper.snapPanOffset(this.panOriginY + (clientY - this.panStartY));
  }

  private isActionTarget(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest(".app-image-lightbox-action") !== null;
  }

  private syncPanWithZoom() {
    if (this.zoom <= 1) {
      this.panX = 0;
      this.panY = 0;
      this.isPanning = false;
    }
  }
}
</script>

<style lang="scss">
// Mounted on document.body, so the overlay's layout CSS lives here rather than
// on the click-target host.
.app-image-lightbox-frame {
  position: relative;
  display: inline-block;
  line-height: 0;
}

.app-image-lightbox-boxes {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
}

.app-image-lightbox-box {
  position: absolute;
  box-sizing: border-box;
  transform-origin: top left;
  border: 2px solid var(--ml-box-color);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.45);
}
</style>

